import { useState, useEffect } from 'react';
import { Hub } from 'aws-amplify/utils';
import { ConsoleLogger } from 'aws-amplify/utils';
import { CONNECTION_STATE_CHANGE, ConnectionState } from '@aws-amplify/pubsub';

import { pubsub } from '../utils/pubsub';
import { readAllMessage, registersInit, REGS } from '../registers';
import { getMacAddressFromUID } from '../components/Common';
import { format, getEnabledState, getLightNumber } from '../components/RegistersHandler';
import { upgradeResultCodes_t } from '../constants';
import { initialConnectionState } from '../state/initialState';
import { devLog } from '../utils/devLog';

export function useDeviceSubscription(currentDevice, upgradeSetters) {
  const {
    setUpdateInProgress,
    setACUpdateInProgress,
    setSH1UpdateInProgress,
    setSH2UpdateInProgress,
    setPowerUpdateInProgress,
    setIOUpdateInProgress,
  } = upgradeSetters;

  const [macAddress, setMacAddress] = useState('');
  const [devTopics, setDevTopics] = useState([]);
  const [devSubscription, setDevSubscription] = useState(null);
  const [registerData, setRegisterData] = useState(registersInit);
  const [connectionState, setConnectionState] = useState(initialConnectionState);
  const [syncing, setSyncing] = useState(false);
  // True once the controller has actually replied to a read-all in this
  // subscription/session. Unlike connectionState.ready (pubsub socket only),
  // this proves the controller itself is online and responding. Reset whenever
  // the subscription re-arms (see the macAddress effect and disconnect()).
  const [controllerResponded, setControllerResponded] = useState(false);

  const handleReceivedData = (topic, data) => {
    const topfromData2 = data.topicSymbol;
    const topicFromData = data[Object.getOwnPropertySymbols(data)[0]]; //data.value.topic;
    devLog('data Rec', data, topicFromData);

    if (topicFromData === topic) //|| (topfromData2 === topic) ){
    {
      const { messageId, modbusReg, modbusVal } = data;
      devLog('received message', messageId, modbusReg, modbusVal);

      const rawData = {};
      let isReadAll = false;
      if (messageId === 'read' && modbusReg === 1 && modbusVal.length % 2 === 0) {
        for (let i = 0; i < modbusVal.length; i += 2) {
          const address = modbusVal[i];
          const value = modbusVal[i + 1];
          rawData[address] = value;
        }
        isReadAll = true;
      } else {
        for (let i = 0; i < modbusVal.length; i++) {
          const address = modbusReg + i;
          const value = modbusVal[i];
          rawData[address] = value;
          devLog('rawData', rawData[address], 'set to ', value);
        }
      }

      for (let i = 0; i < registerData.length; i++) {
        const address = registerData[i].address;

        if (address in rawData) {
          const fmtVal = format(registerData[i], rawData, currentDevice.modelType);
          const enabledVal = getEnabledState(registerData[i], rawData);
          const lightNumber = getLightNumber(registerData[i], rawData);

          if (isReadAll === false) {
            if (address === REGS.FILTER_TIME_ENABLE) {
              /// update the FILTER times ENABLED based on the new enabled times
              // setFilterEnabledValues( rawData, registerData)
              // export const setFilterEnabledValues = (regData, registerData) => {
              const value = rawData[REGS.FILTER_TIME_ENABLE];
              var timerEnabled = false;
              var do_update = false;
              for (let i = 0; i < registerData.length; i++) {
                let theTimerAddress = registerData[i].address;
                do_update = false;
                switch (theTimerAddress) {
                  case REGS.FILTER_TIME_1_START_TIME:
                    timerEnabled = (value & 1) === 1 ? true : false;
                    do_update = true;
                    devLog('setFilterEnabledValues 1', timerEnabled, rawData, value, registerData[i]);
                    break;

                  case REGS.FILTER_TIME_2_START_TIME:
                    timerEnabled = (value & 2) === 2 ? true : false;
                    do_update = true;
                    devLog('setFilterEnabledValues 2', timerEnabled, rawData, value, registerData[i]);
                    break;

                  case REGS.FILTER_TIME_3_START_TIME:
                    timerEnabled = (value & 4) === 4 ? true : false;
                    do_update = true;
                    devLog('setFilterEnabledValues 3', timerEnabled, rawData, value, registerData[i]);
                    break;

                  case REGS.FILTER_TIME_4_START_TIME:
                    timerEnabled = (value & 8) === 8 ? true : false;
                    do_update = true;
                    devLog('setFilterEnabledValues 2', timerEnabled, rawData, value, registerData[i]);
                    break;
                  default:
                    break;
                }
                if (do_update) {
                  const ourUpdate = timerEnabled;
                  devLog('called set fileter ... ', registerData[i], timerEnabled);
                  setRegisterData((prevState) => {
                    const newState = prevState.map((obj) => {
                      if (obj.address === theTimerAddress) {
                        devLog('Found Address:', theTimerAddress, ourUpdate, obj);
                        return { ...obj, enabled: ourUpdate };
                      }
                      return obj;
                    });
                    return newState;
                  });

                  devLog('called set fileter ... ', registerData[i], timerEnabled);
                }
              }

              // }
            }
          }
          setRegisterData((prevState) => {
            // These handlers clear their in-progress flag on UPGRADE_COMPLETE only,
            // deliberately NOT on UPGRADE_ERROR. The flag drives the upgrade card's
            // progress/error rows, so clearing it on error unmounted those rows at the
            // exact moment they had something worth reading — the user never saw the
            // failure. The status register latches UPGRADE_ERROR until the next upgrade
            // starts, so DeviceView treats "flag set + register reads error" as a
            // terminal failure: rows stay up, and the Upgrade button re-enables for a
            // retry (see stillUpgrading there).
            const newState = prevState.map((obj) => {
              // isFresh: did THIS message actually carry this register? The
              // terminal-state checks below must only look at freshly-reported
              // values. Falling back to obj.value made them re-fire against a
              // *cached* one: once an upgrade completed, its register latched
              // UPGRADE_COMPLETE, and every later message — a temperature, another
              // board's progress — re-matched it and cleared the in-progress flag
              // again. The next upgrade's rows opened and closed immediately, and
              // its progress was reported but never shown.
              const isFresh = obj.address in rawData;
              const currentValue = isFresh ? rawData[obj.address] : obj.value;

              if (obj.address === REGS.FWUPDATE) {
                if (isFresh && currentValue === upgradeResultCodes_t.UPGRADE_COMPLETE) {
                  devLog('FW Update state', currentValue);
                  setUpdateInProgress(false);
                }
              } else if (obj.address === REGS.FWUPDATE_EXPANSION_PLUS) {
                if (isFresh && currentValue === upgradeResultCodes_t.UPGRADE_COMPLETE) {
                  devLog('AquaChem FW Update state', currentValue);
                  setACUpdateInProgress(false);
                }
              } else if (obj.address === REGS.FWUPDATE_EXP4_SLAVE1 || obj.address === REGS.FWUPDATE_EXP4_SLAVE2) {
                if (isFresh && currentValue === upgradeResultCodes_t.UPGRADE_COMPLETE) {
                  devLog('EXP4 FW Update state', currentValue);
                  setSH1UpdateInProgress(false);
                  setSH2UpdateInProgress(false);
                }
              }
              // SWC power/IO board upgrades (registers 61440-61443).
              else if (
                obj.address === REGS.FWUPDATE_POWER_BOARD_MAIN ||
                obj.address === REGS.FWUPDATE_POWER_BOARD_BACKUP
              ) {
                if (isFresh && currentValue === upgradeResultCodes_t.UPGRADE_COMPLETE) {
                  devLog('Power board FW Update state', currentValue);
                  setPowerUpdateInProgress?.(false);
                }
              } else if (obj.address === REGS.FWUPDATE_IO_BOARD_MAIN || obj.address === REGS.FWUPDATE_IO_BOARD_BACKUP) {
                if (isFresh && currentValue === upgradeResultCodes_t.UPGRADE_COMPLETE) {
                  devLog('IO board FW Update state', currentValue);
                  setIOUpdateInProgress?.(false);
                }
              }

              if (obj.address === address) {
                if (lightNumber.isFound) {
                  // console.log ( "Found a light number for ", address, lightNumber.number)
                  return {
                    ...obj,
                    value: rawData[address],
                    display: fmtVal,
                    enabled: enabledVal,
                    light_number: lightNumber.number,
                  };
                } else {
                  return { ...obj, value: rawData[address], display: fmtVal, enabled: enabledVal };
                }
              }
              return obj;
            });
            return newState;
          });
        }
      }
      if (isReadAll) {
        // setLoaded(true);
        setSyncing(false);
        setControllerResponded(true);
      }
    } else {
      devLog('topic mismatch, expected', topic, 'received', topfromData2);
    }
  };

  async function doReadAll() {
    const topic = 'dontek' + macAddress + '/cmd/psw';
    await pubsub.publish({
      topics: topic,
      message: readAllMessage,
    });
    devLog('readAll =>', topic);
  }

  function disconnect() {
    if (connectionState.ready) {
      devLog('HandleBack CSR');
      if (devSubscription !== null) {
        devLog('HandleBack devSubscription Unsubscribing', devSubscription);
        devSubscription.unsubscribe();
        setDevSubscription(null);
        setMacAddress(undefined);
        setRegisterData(registersInit);
        setControllerResponded(false);
        return true;
      }
    }
    return false;
  }

  useEffect(() => {
    devLog('macAddress  changed.');
    let sub = null;
    if (macAddress !== undefined) {
      if (macAddress.length !== 0) {
        const newTopic = 'dontek' + macAddress + '/status/psw';
        const builtTopics = [newTopic];
        devLog('useeffect subscribe', builtTopics);
        setRegisterData(registersInit);
        setControllerResponded(false);
        sub = pubsub.subscribe({ topics: builtTopics }).subscribe({
          next: (data) => handleReceivedData(newTopic, data),
          error: (error) => console.error('subscribe error', error),
          complete: () => devLog('subscribe complete'),
        });
        setDevTopics(builtTopics);
        setDevSubscription(sub);
        setUpdateInProgress(false);
        setACUpdateInProgress(false);
        setSH1UpdateInProgress(false);
        setSH2UpdateInProgress(false);
        setPowerUpdateInProgress?.(false);
        setIOUpdateInProgress?.(false);
      } else {
        devLog('Mac Address Length === 0');
      }
    }
    return () => {
      if (sub !== null) {
        devLog('macAddress effect cleanup: unsubscribing');
        sub.unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [macAddress]);

  useEffect(() => {
    devLog('connectionState.ready  changed.');
    if (connectionState.ready && macAddress) {
      doReadAll().catch((error) => console.error('doReadAll failed', error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionState.ready, macAddress]);

  useEffect(() => {
    devLog('currentDevice  changed.');
    if (!currentDevice || Object.keys(currentDevice).length === 0) {
      devLog('No device yet defined ');
    } else {
      if (currentDevice.uniqueDeviceId !== undefined) {
        const macAddr = getMacAddressFromUID(currentDevice.uniqueDeviceId);
        setMacAddress(macAddr);
        devLog('Got Mac Address', macAddr);
        ConsoleLogger.LOG_LEVEL = 'INFO';
        const logger = new ConsoleLogger('foo');
        logger.verbose('info bar');
      } else {
        devLog('NO MAc Address');
      }
    }
  }, [currentDevice]);

  useEffect(() => {
    const removePubsubListener = Hub.listen('pubsub', (data) => {
      const { payload } = data;
      devLog('pubsub:', payload);
      if (payload.event === CONNECTION_STATE_CHANGE) {
        const newConnectionState = payload.data.connectionState;
        devLog('Connenction State Changed:', newConnectionState);
        const ready = newConnectionState === ConnectionState.Connected;
        const state = ConnectionState[newConnectionState];
        setConnectionState({ state, ready });
      }
    });

    return () => {
      removePubsubListener();
    };
  }, []);

  return {
    macAddress,
    devTopics,
    registerData,
    setRegisterData,
    connectionState,
    controllerResponded,
    syncing,
    setSyncing,
    doReadAll,
    disconnect,
  };
}
